import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

// -------------------- THUNKS --------------------

// Fetch employment history for a specific employee
export const fetchEmploymentHistoryByEmployeeId = createAsyncThunk(
  "employeeEmployment/fetchByEmployeeId",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/employee/employment-history/employee/${employeeId}`
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Create new employment history
export const createEmploymentHistory = createAsyncThunk(
  "employeeEmployment/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/employee/employment-history`,
        data
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update employment history
export const updateEmploymentHistory = createAsyncThunk(
  "employeeEmployment/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${API_URL}/employee/employment-history/${id}`,
        data
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Delete employment history
export const deleteEmploymentHistory = createAsyncThunk(
  "employeeEmployment/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/employee/employment-history/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Check Employee Completeness
 * Triggered after save/delete to update overall employee status
 */
export const checkEmployeeCompleteness = createAsyncThunk(
  "employeeEmployment/checkCompleteness",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_URL}/employee/${employeeId}/check-completeness`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Completeness check failed");
    }
  }
);

// -------------------- SLICE --------------------

const initialState = {
  histories: [],
  status: "idle",
  completenessStatus: "idle", // Track completeness separately
  error: null,
};

const employeeEmploymentHistorySlice = createSlice({
  name: "employeeEmploymentHistory",
  initialState,
  reducers: {
    clearEmploymentHistory: (state) => {
      state.histories = [];
      state.status = "idle";
      state.completenessStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* Fetch */
      .addCase(fetchEmploymentHistoryByEmployeeId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.histories = action.payload;
      })
      /* Create */
      .addCase(createEmploymentHistory.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.histories.unshift(action.payload);
      })
      /* Update */
      .addCase(updateEmploymentHistory.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.histories.findIndex((h) => h.id === action.payload.id);
        if (index !== -1) {
          state.histories[index] = action.payload;
        }
      })
      /* Delete */
      .addCase(deleteEmploymentHistory.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.histories = state.histories.filter((h) => h.id !== action.payload);
      })
      /* Completeness Check */
      .addCase(checkEmployeeCompleteness.pending, (state) => {
        state.completenessStatus = "loading";
      })
      .addCase(checkEmployeeCompleteness.fulfilled, (state) => {
        state.completenessStatus = "succeeded";
      })
      .addCase(checkEmployeeCompleteness.rejected, (state, action) => {
        state.completenessStatus = "failed";
        state.error = action.payload;
      })
      /* Global Loading/Error Matchers */
      .addMatcher(
        (action) => action.type.endsWith("/pending") && !action.type.includes("checkCompleteness"),
        (state) => {
          state.status = "loading";
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/rejected") && !action.type.includes("checkCompleteness"),
        (state, action) => {
          state.status = "failed";
          state.error = action.payload || action.error.message;
        }
      );
  },
});

export const { clearEmploymentHistory } = employeeEmploymentHistorySlice.actions;
export default employeeEmploymentHistorySlice.reducer;